import localforage from "localforage";
import { defineStore } from "pinia";
import { LoginAccountInSysOrgDto, SysOrgRoleName } from "~/api";
import { apiClient } from "~/utils/apiClient";
import { useMyInfoStore } from "./MyInfoStore";

export const useLoginAccountsStore = defineStore("LoginAccountsStore", {
  state: (): {
    List: LoginAccountInSysOrgDto[];
    storeLoaded: boolean;
    isRefreshing: boolean;
    lastLoadTime: number;
    listsByKey: Record<string, LoginAccountInSysOrgDto[]>;
    storeLoadedByKey: Record<string, boolean>;
    isRefreshingByKey: Record<string, boolean>;
    lastLoadTimeByKey: Record<string, number>;
  } => {
    return {
      List: [],
      storeLoaded: false,
      isRefreshing: false,
      lastLoadTime: 0,
      listsByKey: {},
      storeLoadedByKey: {},
      isRefreshingByKey: {},
      lastLoadTimeByKey: {},
      // setting lastLoadTime to 0 forces a refresh on browser page reload. This makes the cache only effective during the same page load.
      // setting to Date.now() will make the cache effective for the duration of the cache time.
    };
  },
  actions: {
    getCacheKey(
      roleName: SysOrgRoleName = SysOrgRoleName.MEMBER,
      orgGuid?: string,
    ) {
      const myInfoStore = useMyInfoStore();
      const resolvedOrgGuid = orgGuid ?? myInfoStore.currentSysOrgGuid;
      return `${this.$id}:${resolvedOrgGuid}:${roleName}`;
    },
    syncLegacyState(cacheKey: string) {
      const defaultKey = this.getCacheKey(SysOrgRoleName.MEMBER);
      if (cacheKey !== defaultKey) {
        return;
      }

      this.List = this.listsByKey[cacheKey] ?? [];
      this.storeLoaded = this.storeLoadedByKey[cacheKey] ?? false;
      this.isRefreshing = this.isRefreshingByKey[cacheKey] ?? false;
      this.lastLoadTime = this.lastLoadTimeByKey[cacheKey] ?? 0;
    },
    getList(
      roleName: SysOrgRoleName = SysOrgRoleName.MEMBER,
      orgGuid?: string,
    ): LoginAccountInSysOrgDto[] {
      const cacheKey = this.getCacheKey(roleName, orgGuid);
      return this.listsByKey[cacheKey] ?? [];
    },
    /** Local store for LoginAccounts. This store will cache the data in local storage.
     * @example
     * ```ts
     * import useLoginAccountStore from "~/YYC_Housing/Intake/stores/LoginAccountsStore";
     * const loginAccountsStore = useLoginAccountsStore();
     *
     * onMounted(async () => {
     *  await loginAccountsStore.load();
     * });
     *
     * const list = loginAccountsStore.getList(); // use the list from the store (no need to await)
     * const accountName = loginAccountsStore.getName("YYC"); // use a store function
     * ```
     * @param {boolean} forceUpdate - Can set to true to force a reload from the server.
     */
    async load(
      forceUpdate: boolean = false,
      roleName: SysOrgRoleName = SysOrgRoleName.MEMBER,
      orgGuid?: string,
    ) {
      const cacheKey = this.getCacheKey(roleName, orgGuid);

      if (this.isRefreshingByKey[cacheKey]) {
        // return a promise that will resolve when the store is done refreshing
        return new Promise<void>((resolve, reject) => {
          const checkRefreshing = setInterval(() => {
            if (!this.isRefreshingByKey[cacheKey]) {
              clearInterval(checkRefreshing);
              if (this.storeLoadedByKey[cacheKey] === false) {
                reject(
                  new Error(`Failed to load login accounts for ${cacheKey}`),
                );
                return;
              }
              resolve();
            }
          }, 100); // adjust this if needed
        });
      }

      this.isRefreshingByKey[cacheKey] = true; // set this to true as soon as possible to prevent multiple parallel calls
      this.syncLegacyState(cacheKey);

      // set to 10 minutes
      const MaxCacheTime = 10 * 60 * 1000;

      const myInfoStore = useMyInfoStore();
      const resolvedOrgGuid = orgGuid ?? myInfoStore.currentSysOrgGuid;

      // this is not a sliding expiration, it is a hard expiration. Cache isn't actually gone, but we will refresh it.
      if (Date.now() - (this.lastLoadTimeByKey[cacheKey] ?? 0) > MaxCacheTime) {
        forceUpdate = true;
      }

      if (!forceUpdate && (this.listsByKey[cacheKey]?.length ?? 0) !== 0) {
        this.isRefreshingByKey[cacheKey] = false;
        this.syncLegacyState(cacheKey);
        return;
      }

      try {
        const cached = await localforage.getItem(cacheKey);
        if (cached && !forceUpdate) {
          this.listsByKey[cacheKey] = JSON.parse(cached as string);
          this.storeLoadedByKey[cacheKey] = true;
          this.isRefreshingByKey[cacheKey] = false;
          this.syncLegacyState(cacheKey);
          return;
        }

        // show this message only if we are getting fresh data (keep the console.log)
        console.log("getting fresh", cacheKey);

        // call the correct apiClient method
        this.listsByKey[cacheKey] =
          await apiClient.loginAccount.getLoginAccounts(
            resolvedOrgGuid,
            roleName,
          );

        await localforage.setItem(
          cacheKey,
          JSON.stringify(this.listsByKey[cacheKey]),
        );
        this.storeLoadedByKey[cacheKey] = true;
        this.isRefreshingByKey[cacheKey] = false;
        this.lastLoadTimeByKey[cacheKey] = Date.now();
        this.syncLegacyState(cacheKey);
      } catch (ex) {
        this.listsByKey[cacheKey] = [];
        this.storeLoadedByKey[cacheKey] = false;
        this.isRefreshingByKey[cacheKey] = false;
        this.syncLegacyState(cacheKey);
        throw ex;
      }
    },
    /** Call this after making a change to the underlying data. The next use of the store will get a fresh copy. */
    clear(roleName: SysOrgRoleName = SysOrgRoleName.MEMBER, orgGuid?: string) {
      const cacheKey = this.getCacheKey(roleName, orgGuid);
      this.listsByKey[cacheKey] = [];
      this.storeLoadedByKey[cacheKey] = false;
      this.lastLoadTimeByKey[cacheKey] = 0;
      localforage.removeItem(cacheKey);

      // legacy key from before role/org-specific caching
      if (cacheKey === this.getCacheKey(SysOrgRoleName.MEMBER)) {
        localforage.removeItem(this.$id);
      }

      this.syncLegacyState(cacheKey);
    },
    getName(
      userGuid: string | null | undefined,
      defaultValue: string = "Unknown",
      roleName: SysOrgRoleName = SysOrgRoleName.MEMBER,
      orgGuid?: string,
    ): string {
      if (!userGuid) {
        return defaultValue;
      }
      const account = this.getList(roleName, orgGuid).find(
        (a) => a.userGuid === userGuid,
      );
      return account?.loginName ?? account?.email ?? defaultValue;
    },
  },
});
